import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

// tools
import { storeGet, storeSet } from '../store';
import ProjectService from '../service/project-service';
import { isLocalSubmission, messageToSubmission } from '../helpers/timelineNotes';

const PAGE_SIZE = 100;
const MAX_PAGES = 20;

/** Every TEAM message of the project: the notes are mixed in with the old chat. */
const fetchAllTeamMessages = async (projectId) => {
    const items = [];
    for (let pageId = 0; pageId < MAX_PAGES; pageId += 1) {
        const page = await ProjectService.getProjectMessagesAsync(projectId, { type: 'TEAM', pageId, pageSize: PAGE_SIZE });
        const pageItems = page?.items || [];
        items.push(...pageItems);
        if (!pageItems.length || items.length >= (page?.total ?? 0)) break;
    }
    return items;
};

/**
 * Loads the Pen / Pin / Mood notes already saved on the project into
 * `aiNoteSubmissions`, so the timeline draws their markers across sessions.
 *
 * The key sits under `['project', id, 'messages', 'TEAM']`, which `sendMessage`
 * invalidates, so a new note refreshes the list on its own. Notes still being
 * sent (`local_` ids) are kept on top of the server list.
 */
const useTimelineNotes = (projectId) => {
    const { data } = useQuery({
        queryKey: ['project', projectId, 'messages', 'TEAM', 'timeline-notes'],
        queryFn: () => fetchAllTeamMessages(projectId),
        enabled: !!projectId,
    });

    useEffect(() => {
        storeSet('aiNoteSubmissions', []);
    }, [projectId]);

    useEffect(() => {
        if (!data) return;
        const saved = data.map(messageToSubmission).filter(Boolean);
        const pending = (storeGet('aiNoteSubmissions') || []).filter(isLocalSubmission);
        storeSet('aiNoteSubmissions', [...pending, ...saved]);
    }, [data]);
};

export default useTimelineNotes;
